/* Bestanden van het project, per document met zijn versies (ADR 0017).
 *
 * Een versie is altijd een eigen bestand naast de vorige; welke bestanden bij
 * één document horen volgt uit de naam (fileVersions.ts). Per versie staat wat er
 * veranderd is en via welke weg (MCP of deze pagina). Een nieuwe versie vraagt
 * altijd om die omschrijving, net als new_version in gv-crm-mcp.
 *
 * Een nieuwe versie is standaard een kleine update (3.1 → 3.1.1); een grote
 * (→ 3.2) kies je er zelf bij (Stan, 02-10-2026). In de tijdlijn staat een kleine
 * update ingesprongen boven de 3.1 waar hij bij hoort.
 *
 * Slepen: op een document = nieuwe versie daarvan; op het vak onderaan = nieuw
 * bestand in de gekozen map. Is dat nieuwe bestand een hogere versie van iets dat
 * er al staat, dan wordt het vanzelf een nieuwe versie en is de omschrijving
 * verplicht.
 *
 * Feedback (ADR 0021): per document het aantal open punten, "Feedback" opent de
 * documentweergave en "Vraag feedback" de ping. Per versie staat wie hem plaatste,
 * hoeveel punten erop staan en aan wie feedback gevraagd is. */

import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  ChevronDown, ChevronRight, ExternalLink, FileText, Image as ImageIcon, History, Upload, AlertTriangle, Loader2, MessageSquareText, BellRing, Send,
} from 'lucide-react';
import { verstuurdTekst, type Verstuurd } from '@/features/opvolging/data/opvolging';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { FileVersionError, type DocumentGroup } from '../../../../supabase/functions/_shared/fileVersions';
import { doelVersie, versieStappen, vorigeVersie, type Bestand, type Bestanden, type VersieKeuze, type VersieStap } from '../data/bestanden';
import { namenLijst, openVoorMij, puntenVan, telling, verzoekenVan, type FeedbackOverzicht } from '../data/feedback';
import { useUpload } from '../hooks';
import { datum, extensie, grootte, IS_BEELD } from './opmaak';

export interface BestandenBlokHandle {
  /** Scrolt naar het blok en opent de bestandskiezer. */
  kiesBestand(): void;
}

type Formulier =
  | { soort: 'nieuw'; bestand: File | null; map: string; omschrijving: string }
  | { soort: 'versie'; docKey: string; variant: number; bestand: File | null; stap: VersieStap; versie: string; concept: boolean | null; omschrijving: string };

const MIN_OMSCHRIJVING = 10;

/** Wat het versieformulier kiest. Buiten de GV-conventie is er geen klein of groot: een ingevuld nummer telt, anders één omhoog. */
function keuzeVan(f: Extract<Formulier, { soort: 'versie' }>, isGv: boolean): VersieKeuze {
  if (!isGv) return { stap: f.versie.trim() ? 'eigen' : 'klein', versie: f.versie };
  return { stap: f.stap, versie: f.versie, concept: f.concept ?? undefined };
}

function Icoon({ naam }: { naam: string }) {
  const ext = extensie(naam);
  if (IS_BEELD.has(ext)) return <ImageIcon className="h-[18px] w-[18px] shrink-0 text-sky-700" aria-hidden />;
  return <FileText className={cn('h-[18px] w-[18px] shrink-0', ext === 'pdf' ? 'text-red-700' : 'text-slate-600')} aria-hidden />;
}

const mapLabel = (m: string) => (m === '' ? 'Projectmap' : m);

/** Wie een versie plaatste (project_file_versions.author_id); niet via welke weg (Stan, 29-09). */
export function doorLabel(r: { author_name: string | null } | undefined): string | null {
  return r?.author_name ? `door ${r.author_name}` : null;
}

export const BestandenBlok = forwardRef<BestandenBlokHandle, {
  projectId: string;
  data: Bestanden | undefined;
  isLoading: boolean;
  error: unknown;
  onOpnieuw: () => void;
  magVersies: boolean;
  /** Feedback van het project (migratie 098); undefined zolang hij laadt. */
  feedback?: FeedbackOverzicht;
  mijnId?: string | null;
  onOpenDocument?: (fileId: string) => void;
  onVraagFeedback?: (doc: DocumentGroup<Bestand>, fileId?: string) => void;
  /** Welke bestanden aan een relatie zijn verstuurd, per bestandsnaam in kleine letters (ADR 0030). */
  verstuurd?: Map<string, Verstuurd>;
}>(function BestandenBlok({ projectId, data, isLoading, error, onOpnieuw, magVersies, feedback, mijnId, onOpenDocument, onVraagFeedback, verstuurd }, ref) {
  const { toast } = useToast();
  const upload = useUpload(projectId);
  const sectie = useRef<HTMLElement>(null);
  const kiezer = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState<string>('*');
  const [open, setOpen] = useState<string | null>(null);
  const [formulier, setFormulier] = useState<Formulier | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [sleepDoel, setSleepDoel] = useState<string | null>(null);

  const kanSchrijven = data?.bron === 'sharepoint';
  const standaardMap = filter === '*' ? '' : filter;

  useImperativeHandle(ref, () => ({
    kiesBestand() {
      sectie.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (kanSchrijven) kiezer.current?.click();
    },
  }));

  const mapTelling = useMemo(() => {
    const t = new Map<string, number>();
    for (const d of data?.documenten ?? []) t.set(d.folder, (t.get(d.folder) ?? 0) + 1);
    return t;
  }, [data]);
  /* Alle mappen voor het uploaden; als filter alleen mappen met iets erin. */
  const mappen = useMemo(() => {
    const alle = new Set<string>(['', ...(data?.mappen ?? []), ...mapTelling.keys()]);
    return [...alle].sort((a, b) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b, 'nl')));
  }, [data, mapTelling]);
  const filterMappen = mappen.filter((m) => (mapTelling.get(m) ?? 0) > 0);
  const documenten = (data?.documenten ?? []).filter((d) => filter === '*' || d.folder === filter);

  const startNieuw = (bestand: File | null, map = standaardMap) => {
    setFout(null);
    setFormulier({ soort: 'nieuw', bestand, map, omschrijving: '' });
  };
  const startVersie = (doc: DocumentGroup<Bestand>, bestand: File | null = null) => {
    setFout(null);
    setOpen(doc.key);
    setFormulier({ soort: 'versie', docKey: doc.key, variant: 0, bestand, stap: 'klein', versie: '', concept: null, omschrijving: '' });
  };

  const verstuur = () => {
    if (!formulier || !formulier.bestand) return;
    setFout(null);
    const klaar = (naam: string) => {
      toast({ title: formulier.soort === 'versie' ? 'Nieuwe versie opgeslagen' : 'Bestand opgeslagen', description: naam });
      setFormulier(null);
    };
    const mislukt = (e: unknown) => setFout((e as Error).message);
    if (formulier.soort === 'nieuw') {
      upload.mutate(
        { projectId, map: formulier.map, bestand: formulier.bestand, modus: 'upload', omschrijving: formulier.omschrijving.trim() || undefined },
        { onSuccess: (r) => klaar(r.name), onError: mislukt },
      );
      return;
    }
    const doc = data?.documenten.find((d) => d.key === formulier.docKey);
    const variant = doc?.versions[0]?.files[formulier.variant];
    const vorige = variant?.file;
    if (!doc || !vorige) return;
    /* Het nummer gaat altijd uitgeschreven mee: wat bij "Wordt:" staat, is wat er komt. */
    let doel: { naam: string; versie: string };
    try {
      doel = doelVersie(vorige.name, keuzeVan(formulier, variant.parsed.scheme === 'gv'));
    } catch (e) {
      mislukt(e);
      return;
    }
    upload.mutate(
      {
        projectId,
        map: doc.folder,
        bestand: formulier.bestand,
        modus: 'new_version',
        vorige: vorige.name,
        omschrijving: formulier.omschrijving.trim(),
        versie: doel.versie,
        concept: formulier.concept ?? undefined,
      },
      { onSuccess: (r) => klaar(r.name), onError: mislukt },
    );
  };

  const sleepProps = (doel: string, bijLos: (f: File) => void) => ({
    onDragOver: (e: React.DragEvent) => {
      if (!kanSchrijven || !e.dataTransfer.types.includes('Files')) return;
      e.preventDefault();
      e.stopPropagation();
      setSleepDoel(doel);
    },
    onDragLeave: () => setSleepDoel((d) => (d === doel ? null : d)),
    onDrop: (e: React.DragEvent) => {
      if (!kanSchrijven) return;
      e.preventDefault();
      e.stopPropagation();
      setSleepDoel(null);
      const f = e.dataTransfer.files?.[0];
      if (f) bijLos(f);
    },
  });

  const aantalDocs = data?.documenten.length ?? 0;
  const metFeedback = Boolean(feedback?.beschikbaar && onOpenDocument);

  return (
    <section ref={sectie} id="bestanden" aria-labelledby="pagina-bestanden" className="flex scroll-mt-14 flex-col gap-3.5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="pagina-bestanden" className="text-lg font-semibold">
          Bestanden <span className="font-normal text-muted-foreground">{aantalDocs}</span>
        </h2>
        {filterMappen.length > 1 && (
          <div role="group" aria-label="Map" className="flex flex-wrap gap-1.5 text-[13px]">
            {['*', ...filterMappen].map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={filter === m}
                onClick={() => setFilter(m)}
                className={cn(
                  'h-[30px] rounded-full border px-3',
                  filter === m ? 'border-foreground bg-foreground font-medium text-background' : 'bg-background text-muted-foreground hover:text-foreground',
                )}
              >
                {m === '*' ? 'Alles' : mapLabel(m)} <span className="opacity-70">{m === '*' ? aantalDocs : mapTelling.get(m) ?? 0}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <input
        ref={kiezer}
        type="file"
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          if (formulier) setFormulier({ ...formulier, bestand: f });
          else startNieuw(f);
        }}
      />

      {data?.melding && (
        <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {data.melding}
        </p>
      )}

      <div className="rounded-xl border bg-background">
        {isLoading ? (
          <div className="flex flex-col gap-3 p-5" aria-busy>
            {[0, 1, 2].map((i) => <div key={i} className="h-5 animate-pulse rounded bg-muted" />)}
          </div>
        ) : error ? (
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-5 text-sm">
            <span className="text-muted-foreground">Bestanden konden niet geladen worden: {(error as Error).message}</span>
            <Button variant="outline" size="sm" onClick={onOpnieuw}>Opnieuw</Button>
          </div>
        ) : documenten.length === 0 ? (
          <p className="px-4 py-3 text-sm text-muted-foreground">{filter === '*' ? 'De projectmap is nog leeg.' : 'Geen bestanden in deze map.'}</p>
        ) : (
          <>
            <div className="hidden gap-4 border-b px-5 py-2.5 text-xs text-muted-foreground md:grid md:grid-cols-[minmax(0,1fr)_150px_110px_80px_150px]">
              <span className="pl-8">Document</span><span>Map</span><span>Gewijzigd</span><span>Grootte</span><span />
            </div>
            <ul className="divide-y">
              {documenten.map((doc) => {
                const nieuwste = doc.versions[0]!;
                const hoofd = nieuwste.files[0]!;
                const meerdere = doc.versions.length > 1;
                const toonVersie = meerdere || doc.scheme !== 'none';
                const isOpen = open === doc.key;
                const regel = data?.versies.get(hoofd.file.id);
                const inFormulier = formulier?.soort === 'versie' && formulier.docKey === doc.key;
                const punten = metFeedback ? puntenVan(doc, feedback!.punten) : [];
                const verzoeken = metFeedback ? verzoekenVan(doc, feedback!.verzoeken) : [];
                const tel = telling(punten);
                const gevraagdAanMij = openVoorMij(verzoeken, mijnId).length > 0;
                return (
                  <li
                    key={doc.key}
                    {...sleepProps(doc.key, (f) => (magVersies ? startVersie(doc, f) : startNieuw(f, doc.folder)))}
                    className={cn(sleepDoel === doc.key && 'bg-blue-50 ring-2 ring-inset ring-blue-400')}
                  >
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 px-5 py-3 text-sm md:grid-cols-[minmax(0,1fr)_150px_110px_80px_150px]">
                      <div className="flex min-w-0 items-center gap-2">
                        <button
                          type="button"
                          aria-expanded={isOpen}
                          aria-label={isOpen ? 'Versies inklappen' : 'Versies tonen'}
                          onClick={() => setOpen(isOpen ? null : doc.key)}
                          className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted"
                        >
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                        <Icoon naam={hoofd.file.name} />
                        <div className="flex min-w-0 flex-col">
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <a href={hoofd.file.web_url ?? undefined} target="_blank" rel="noreferrer" className="truncate font-medium hover:underline">
                              {doc.scheme === 'none' && !meerdere ? hoofd.file.name : doc.title}
                            </a>
                            {toonVersie && (
                              <span className="rounded bg-foreground px-1.5 py-px text-[11px] font-semibold text-background">v{nieuwste.version}</span>
                            )}
                            {nieuwste.concept && (
                              <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-px text-[10px] font-bold tracking-wide text-amber-800">CONCEPT</span>
                            )}
                            {nieuwste.files.length > 1 && nieuwste.files.map((f) => (
                              <a key={f.file.id} href={f.file.web_url ?? undefined} target="_blank" rel="noreferrer"
                                className="rounded border px-1.5 py-px text-[11px] font-semibold hover:bg-muted">
                                {f.parsed.lang ?? extensie(f.file.name).toUpperCase()}
                              </a>
                            ))}
                            {meerdere && <span className="text-xs text-muted-foreground">{doc.versions.length} versies</span>}
                            {gevraagdAanMij && (
                              <button type="button" onClick={() => onOpenDocument!(hoofd.file.id)}
                                className="flex items-center gap-1 rounded-full bg-blue-100 px-2 py-px text-[11px] font-medium text-blue-900 hover:bg-blue-200">
                                <BellRing className="h-3 w-3" aria-hidden />feedback gevraagd
                              </button>
                            )}
                            {tel.totaal > 0 && (
                              <button type="button" onClick={() => onOpenDocument!(hoofd.file.id)}
                                aria-label={`${tel.open} open van ${tel.totaal} ${tel.totaal === 1 ? 'feedbackpunt' : 'feedbackpunten'}`}
                                className={cn('flex items-center gap-1 rounded-full px-2 py-px text-[11px] font-medium',
                                  tel.open > 0 ? 'bg-amber-100 text-amber-900 hover:bg-amber-200' : 'bg-muted text-muted-foreground hover:text-foreground')}>
                                <MessageSquareText className="h-3 w-3" aria-hidden />{tel.open > 0 ? `${tel.open} open` : tel.totaal}
                              </button>
                            )}
                          </div>
                          {regel && !isOpen && <span className="truncate text-xs text-muted-foreground">{regel.message}</span>}
                        </div>
                      </div>
                      <span className="hidden truncate text-muted-foreground md:block">{mapLabel(doc.folder)}</span>
                      <span className="hidden text-muted-foreground md:block">{datum(hoofd.file.modified)}</span>
                      <span className="hidden text-muted-foreground md:block">{grootte(hoofd.file.size)}</span>
                      <span className="flex justify-end gap-1">
                        {metFeedback && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Feedback op ${doc.title}`} title="Feedback" onClick={() => onOpenDocument!(hoofd.file.id)}>
                            <MessageSquareText className="h-4 w-4" />
                          </Button>
                        )}
                        {metFeedback && onVraagFeedback && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Vraag feedback op ${doc.title}`} title="Vraag feedback" onClick={() => onVraagFeedback(doc)}>
                            <BellRing className="h-4 w-4" />
                          </Button>
                        )}
                        {magVersies && kanSchrijven && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Nieuwe versie van ${doc.title}`} onClick={() => startVersie(doc)}>
                            <History className="h-4 w-4" />
                          </Button>
                        )}
                        {hoofd.file.web_url && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                            <a href={hoofd.file.web_url} target="_blank" rel="noreferrer" aria-label={`${hoofd.file.name} openen in SharePoint`}>
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                      </span>
                    </div>

                    {isOpen && (
                      <div className="border-t bg-muted/30 px-5 pb-4 pt-3 md:pl-[76px]">
                        {inFormulier && formulier.soort === 'versie' && (
                          <VersieFormulier
                            doc={doc}
                            formulier={formulier}
                            bezig={upload.isPending}
                            fout={fout}
                            onWijzig={setFormulier}
                            onKies={() => kiezer.current?.click()}
                            onOpslaan={verstuur}
                            onAnnuleer={() => { setFormulier(null); setFout(null); }}
                          />
                        )}
                        <ol className="flex flex-col">
                          {doc.versions.map((v, i) => {
                            const beschrijving = v.files.map((f) => data?.versies.get(f.file.id)).find(Boolean);
                            const hier = punten.filter((p) => v.files.some((f) => f.file.id === p.file_id));
                            const telHier = telling(hier);
                            const gevraagd = verzoeken.filter((q) => v.files.some((f) => f.file.id === q.file_id));
                            /* Uit de eigen mail (NATS): de bijlagenaam is de naam van dit bestand. */
                            const isVerstuurd = v.files.map((f) => verstuurd?.get(f.file.name.normalize('NFC').trim().toLowerCase())).find(Boolean);
                            /* 3.1.1 en 3.1.2 zijn updates van hun 3.1: kleiner en ingesprongen. */
                            const klein = v.updateOf !== null;
                            return (
                              <li key={v.version} className="grid grid-cols-[18px_minmax(0,1fr)] gap-3 py-2 md:grid-cols-[18px_minmax(0,1fr)_auto]">
                                <span className="flex flex-col items-center pt-1.5" aria-hidden>
                                  <span className={cn(
                                    'rounded-full',
                                    klein ? 'my-0.5 h-1.5 w-1.5' : 'h-2.5 w-2.5',
                                    i === 0 ? 'bg-foreground' : klein ? 'bg-slate-400' : 'border-2 border-slate-400 bg-background',
                                  )} />
                                  {i < doc.versions.length - 1 && <span className="mt-1 w-0.5 flex-1 bg-border" />}
                                </span>
                                <div className={cn('flex min-w-0 flex-col gap-1', klein && 'pl-5')}>
                                  <div className="flex flex-wrap items-center gap-2 text-sm">
                                    <strong className={cn(klein && 'font-semibold')}>v{v.version}</strong>
                                    {klein && <span className="rounded border bg-background px-1.5 py-px text-[11px] text-muted-foreground">update van v{v.updateOf}</span>}
                                    {v.concept && <span className="text-[10px] font-bold tracking-wide text-amber-800">CONCEPT</span>}
                                    <span className="text-xs text-muted-foreground">
                                      {datum(beschrijving?.created_at ?? v.files[0]!.file.modified)}
                                      {doorLabel(beschrijving) ? ` · ${doorLabel(beschrijving)}` : ''}
                                    </span>
                                  </div>
                                  <p className={cn('m-0 max-w-[640px] text-[13px] leading-relaxed', beschrijving ? 'text-foreground/80' : 'italic text-muted-foreground')}>
                                    {beschrijving?.message ?? 'Geen omschrijving vastgelegd.'}
                                  </p>
                                  {isVerstuurd && (
                                    <p className="m-0 flex items-center gap-1.5 text-xs text-muted-foreground">
                                      <Send className="h-3 w-3" aria-hidden />
                                      {verstuurdTekst(isVerstuurd)}
                                    </p>
                                  )}
                                  {metFeedback && (telHier.totaal > 0 || gevraagd.length > 0) && (
                                    <p className="m-0 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                                      {telHier.totaal > 0 && (
                                        <button type="button" className="font-medium text-foreground underline-offset-2 hover:underline" onClick={() => onOpenDocument!(v.files[0]!.file.id)}>
                                          {telHier.totaal === 1 ? '1 feedbackpunt' : `${telHier.totaal} feedbackpunten`}{telHier.open > 0 ? `, ${telHier.open} open` : ''}
                                        </button>
                                      )}
                                      {gevraagd.map((q) => (
                                        <span key={q.id}>
                                          feedback gevraagd aan {namenLijst(q.recipients.map((r) => `${r.name ?? 'iemand'}${r.done ? ' ✓' : ''}`))} ({datum(q.created_at)})
                                        </span>
                                      ))}
                                    </p>
                                  )}
                                </div>
                                <span className="col-start-2 flex flex-wrap items-start gap-1.5 self-start md:col-start-auto md:justify-end">
                                  {v.files.map((f) => (
                                    <a key={f.file.id} href={f.file.web_url ?? undefined} target="_blank" rel="noreferrer"
                                      className="whitespace-nowrap rounded-md border bg-background px-2 py-0.5 text-xs font-medium hover:bg-muted"
                                      title={f.file.name}>
                                      {f.parsed.lang ?? extensie(f.file.name).toUpperCase()} · {grootte(f.file.size)}
                                    </a>
                                  ))}
                                </span>
                              </li>
                            );
                          })}
                        </ol>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}

        {formulier?.soort === 'nieuw' && (
          <NieuwFormulier
            formulier={formulier}
            mappen={mappen}
            bestanden={data?.bestanden ?? []}
            magVersies={magVersies}
            bezig={upload.isPending}
            fout={fout}
            onWijzig={setFormulier}
            onKies={() => kiezer.current?.click()}
            onOpslaan={verstuur}
            onAnnuleer={() => { setFormulier(null); setFout(null); }}
          />
        )}

        {kanSchrijven && !formulier && (
          <div
            {...sleepProps('__nieuw', (f) => startNieuw(f))}
            className={cn(
              'm-4 mt-3 flex min-h-[60px] flex-wrap items-center justify-center gap-2 rounded-[10px] border-[1.5px] border-dashed px-4 text-sm text-muted-foreground',
              sleepDoel === '__nieuw' ? 'border-blue-400 bg-blue-50 text-blue-900' : 'border-slate-300',
            )}
          >
            <Upload className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">Sleep een bestand hierheen of</span>
            <button type="button" className="font-medium text-foreground underline underline-offset-2" onClick={() => kiezer.current?.click()}>
              <span className="sm:hidden">Bestand kiezen</span>
              <span className="hidden sm:inline">kies er een</span>
            </button>
            <span className="hidden sm:inline">· op een document slepen = nieuwe versie</span>
          </div>
        )}
      </div>
      {data?.afgekapt && <p className="text-xs text-muted-foreground">Niet alle submappen zijn doorzocht (te diep of te veel bestanden); open SharePoint voor de rest.</p>}
    </section>
  );
});

function Foutregel({ fout }: { fout: string | null }) {
  if (!fout) return null;
  return <p role="alert" className="m-0 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{fout}</p>;
}

function VersieFormulier({ doc, formulier, bezig, fout, onWijzig, onKies, onOpslaan, onAnnuleer }: {
  doc: DocumentGroup<Bestand>;
  formulier: Extract<Formulier, { soort: 'versie' }>;
  bezig: boolean;
  fout: string | null;
  onWijzig: (f: Formulier) => void;
  onKies: () => void;
  onOpslaan: () => void;
  onAnnuleer: () => void;
}) {
  const varianten = doc.versions[0]!.files;
  const vorige = varianten[formulier.variant]!;
  const isGv = vorige.parsed.scheme === 'gv';
  const stappen = isGv ? versieStappen(vorige.file.name) : null;
  let doelnaam: string | null = null;
  let naamFout: string | null = null;
  try {
    doelnaam = doelVersie(vorige.file.name, keuzeVan(formulier, isGv)).naam;
  } catch (e) {
    naamFout = e instanceof FileVersionError ? e.message : (e as Error).message;
  }
  /* "Eigen nummer" gekozen maar nog niets getypt: een aanwijzing, geen fout. */
  const nogLeeg = isGv && formulier.stap === 'eigen' && !formulier.versie.trim();
  const keuzes: { stap: VersieStap; label: string; nummer: string | null }[] = [
    { stap: 'klein', label: 'Kleine update', nummer: stappen?.klein ?? null },
    { stap: 'groot', label: 'Grote update', nummer: stappen?.groot ?? null },
    { stap: 'eigen', label: 'Eigen nummer', nummer: null },
  ];
  const genoeg = formulier.omschrijving.trim().length >= MIN_OMSCHRIJVING;
  const kan = !bezig && formulier.bestand && genoeg && doelnaam;

  return (
    <div className="mb-3 flex flex-col gap-3 rounded-[10px] border border-blue-200 bg-blue-50/70 p-4">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <strong>Nieuwe versie</strong>
        {varianten.length > 1 && (
          <label className="flex items-center gap-2 text-[13px]">
            <span className="text-muted-foreground">van</span>
            <select
              className="h-8 rounded-md border bg-background px-2"
              value={formulier.variant}
              onChange={(e) => onWijzig({ ...formulier, variant: Number(e.target.value) })}
            >
              {varianten.map((f, i) => <option key={f.file.id} value={i}>{f.parsed.lang ?? extensie(f.file.name).toUpperCase()}</option>)}
            </select>
          </label>
        )}
        {isGv && (
          <label className="flex items-center gap-2 text-[13px]">
            <Checkbox
              checked={formulier.concept ?? vorige.parsed.concept}
              onCheckedChange={(c) => onWijzig({ ...formulier, concept: c === true })}
            />
            CONCEPT
          </label>
        )}
        {!isGv && (
          <label className="flex items-center gap-2 text-[13px]">
            <span className="text-muted-foreground">versie</span>
            <Input
              value={formulier.versie}
              onChange={(e) => onWijzig({ ...formulier, versie: e.target.value })}
              placeholder="automatisch"
              className="h-8 w-28 bg-background"
            />
          </label>
        )}
      </div>
      {isGv && (
        <div className="flex flex-wrap items-center gap-2 text-[13px]">
          <div role="radiogroup" aria-label="Soort update" className="flex flex-wrap gap-1.5">
            {keuzes.map((k) => (
              <button
                key={k.stap}
                type="button"
                role="radio"
                aria-checked={formulier.stap === k.stap}
                onClick={() => onWijzig({ ...formulier, stap: k.stap })}
                className={cn(
                  'flex h-8 items-center gap-1.5 rounded-full border px-3',
                  formulier.stap === k.stap ? 'border-foreground bg-foreground font-medium text-background' : 'bg-background text-muted-foreground hover:text-foreground',
                )}
              >
                {k.label}
                {k.nummer && <span className="opacity-70">v{k.nummer}</span>}
              </button>
            ))}
          </div>
          {formulier.stap === 'eigen' && (
            <Input
              autoFocus
              aria-label="Versienummer"
              value={formulier.versie}
              onChange={(e) => onWijzig({ ...formulier, versie: e.target.value })}
              placeholder="bijv. 4.0"
              className="h-8 w-28 bg-background"
            />
          )}
        </div>
      )}
      <p className="m-0 break-all text-[13px] text-blue-900">
        {doelnaam
          ? <>Wordt: <span className="font-medium">{doelnaam}</span></>
          : <span className={nogLeeg ? 'text-muted-foreground' : 'text-red-800'}>{naamFout}</span>}
      </p>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`omschrijving-${doc.key}`}>Wat is er veranderd?</Label>
        <Textarea
          id={`omschrijving-${doc.key}`}
          value={formulier.omschrijving}
          onChange={(e) => onWijzig({ ...formulier, omschrijving: e.target.value })}
          placeholder="Bijvoorbeeld: financiële pagina toegevoegd, tarieven 2027 verwerkt."
          rows={2}
          className="bg-background"
        />
        {!genoeg && formulier.omschrijving.length > 0 && (
          <span className="text-xs text-muted-foreground">Minimaal {MIN_OMSCHRIJVING} tekens; dit komt in de versiegeschiedenis en het logboek.</span>
        )}
      </div>
      <Foutregel fout={fout} />
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onKies} className="bg-background">
          {formulier.bestand ? `Ander bestand (${formulier.bestand.name})` : 'Kies bestand'}
        </Button>
        <Button type="button" size="sm" disabled={!kan} onClick={onOpslaan}>
          {bezig && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Versie opslaan
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onAnnuleer}>Annuleren</Button>
      </div>
    </div>
  );
}

function NieuwFormulier({ formulier, mappen, bestanden, magVersies, bezig, fout, onWijzig, onKies, onOpslaan, onAnnuleer }: {
  formulier: Extract<Formulier, { soort: 'nieuw' }>;
  mappen: string[];
  bestanden: Bestand[];
  magVersies: boolean;
  bezig: boolean;
  fout: string | null;
  onWijzig: (f: Formulier) => void;
  onKies: () => void;
  onOpslaan: () => void;
  onAnnuleer: () => void;
}) {
  const vorige = formulier.bestand ? vorigeVersie(bestanden, formulier.map, formulier.bestand.name) : null;
  const bestaat = formulier.bestand
    ? bestanden.some((b) => b.folder === formulier.map && b.name.toLowerCase() === formulier.bestand!.name.toLowerCase())
    : false;
  const nodig = vorige ? MIN_OMSCHRIJVING : 0;
  const genoeg = formulier.omschrijving.trim().length >= nodig;
  const kan = !bezig && formulier.bestand && genoeg && !bestaat && !(vorige && !magVersies);

  return (
    <div className="m-4 flex flex-col gap-3 rounded-[10px] border bg-muted/40 p-4">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <strong>{vorige ? 'Nieuwe versie' : 'Nieuw bestand'}</strong>
        <span className="break-all text-muted-foreground">{formulier.bestand?.name ?? 'nog geen bestand gekozen'}</span>
        <label className="flex items-center gap-2 text-[13px]">
          <span className="text-muted-foreground">in</span>
          <select className="h-8 rounded-md border bg-background px-2" value={formulier.map} onChange={(e) => onWijzig({ ...formulier, map: e.target.value })}>
            {mappen.map((m) => <option key={m} value={m}>{mapLabel(m)}</option>)}
          </select>
        </label>
      </div>
      {bestaat && (
        <p className="m-0 text-sm text-red-800">Deze naam staat al in {mapLabel(formulier.map)}. Overschrijven kan niet: sleep het bestand op het document voor een nieuwe versie.</p>
      )}
      {vorige && !bestaat && (
        <p className="m-0 text-sm text-blue-900">
          Dit is een nieuwe versie van <span className="font-medium">{vorige.name}</span> (v{vorige.version}).
          {!magVersies && ' Een nieuwe versie zetten mag alleen een admin.'}
        </p>
      )}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="omschrijving-nieuw">{vorige ? 'Wat is er veranderd?' : 'Omschrijving (optioneel)'}</Label>
        <Textarea
          id="omschrijving-nieuw"
          value={formulier.omschrijving}
          onChange={(e) => onWijzig({ ...formulier, omschrijving: e.target.value })}
          placeholder={vorige ? 'Bijvoorbeeld: scenario C toegevoegd na overleg met de netbeheerder.' : 'Wat is dit bestand?'}
          rows={2}
          className="bg-background"
        />
      </div>
      <Foutregel fout={fout} />
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onKies} className="bg-background">
          {formulier.bestand ? 'Ander bestand' : 'Kies bestand'}
        </Button>
        <Button type="button" size="sm" disabled={!kan} onClick={onOpslaan}>
          {bezig && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {vorige ? 'Versie opslaan' : 'Uploaden'}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onAnnuleer}>Annuleren</Button>
      </div>
    </div>
  );
}
